import * as THREE from 'three';
import { HDRLoader } from 'three/examples/jsm/loaders/HDRLoader.js';
import type { FloatImage } from '../look/sky.js';
import { modelLoader } from '../models/builder.js';
import type { Fetchers } from './world.js';

/**
 * How a headset fetches a world's files: from `/worlds/` on its own origin
 * (the CSP's 'self') or a package's own URLs, plain glTF with no decoder (no WebAssembly), Radiance
 * skies as floats for the CPU to bake. The service worker caches them. `inMemory` (the desktop
 * preview's fixtures, which the CSP's connect-src cannot fetch from a `blob:`): files held in memory by URL.
 */
export function browserFetchers(inMemory?: ReadonlyMap<string, ArrayBuffer>): Fetchers {
  const textures = new THREE.TextureLoader();
  // Images through `<img>`, as the CSP allows (a package's GLBs carry textures; library models do too).
  const models = modelLoader();
  const skies = new HDRLoader().setDataType(THREE.FloatType);
  return {
    texture: (url) => textures.loadAsync(url),
    model: async (url) => {
      const held = inMemory?.get(url);
      return held ? (await models.parseAsync(held, '')).scene : (await models.loadAsync(url)).scene;
    },
    hdri: async (url): Promise<FloatImage> => {
      const texture = await skies.loadAsync(url);
      const image = texture.image as { width: number; height: number; data: Float32Array };
      const out = { width: image.width, height: image.height, data: image.data };
      texture.dispose();
      return out;
    },
  };
}
